import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  FileText, Loader2, Download, FileType2, Sparkles, Check, BookOpen, Copy,
} from "lucide-react";
import api, { apiErr } from "../lib/api";
import Markdown from "../components/Markdown";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";

export default function ExportStudio() {
  const [favs, setFavs] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [title, setTitle] = useState("");
  const [instructions, setInstructions] = useState("");
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [doc, setDoc] = useState(null);

  useEffect(() => {
    api.get("/favorites")
      .then(({ data }) => setFavs(data))
      .catch((e) => toast.error(apiErr(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const generate = async () => {
    if (selected.size === 0) { toast.error("Select at least one saved clip."); return; }
    if (!title.trim()) { toast.error("Give your document a working title."); return; }
    setGenerating(true);
    setDoc(null);
    try {
      const { data } = await api.post("/export", {
        title: title.trim(),
        clip_ids: Array.from(selected),
        instructions,
      });
      setDoc(data);
      toast.success("Draft generated by Claude");
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    } finally {
      setGenerating(false);
    }
  };

  const downloadMarkdown = () => {
    if (!doc) return;
    const blob = new Blob([doc.content], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(doc.title || "document").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadPdf = () => {
    if (!doc) return;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Allow pop-ups to export PDF."); return; }
    const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
    w.document.write(`<!doctype html><html><head><title>${esc(doc.title)}</title>
      <style>body{font-family:Georgia,serif;max-width:720px;margin:48px auto;padding:0 24px;line-height:1.6;color:#111}
      h1,h2,h3{font-family:Georgia,serif;line-height:1.2}code{font-family:monospace;background:#f1f1f1;padding:1px 4px;border-radius:4px}</style>
      </head><body><pre style="white-space:pre-wrap;font-family:Georgia,serif;border:0">${esc(doc.content)}</pre>
      <script>window.onload=function(){window.print();}</script></body></html>`);
    w.document.close();
  };

  const copyMd = async () => {
    if (!doc) return;
    await navigator.clipboard.writeText(doc.content);
    toast.success("Markdown copied");
  };

  return (
    <div>
      <p className="font-mono text-xs uppercase tracking-widest text-emerald-400 mb-2">foundation for your writing</p>
      <h1 className="font-serif text-3xl sm:text-4xl font-extrabold tracking-tight text-zinc-50 mb-2">Document Studio</h1>
      <p className="text-zinc-400 mb-8 max-w-2xl leading-relaxed">
        Stack your saved clips, set a direction, and let Claude forge a structured draft you can grow into an article or book.
      </p>

      <div className="grid lg:grid-cols-[minmax(0,420px)_1fr] gap-6 items-start">
        {/* builder */}
        <div className="space-y-4">
          <div className="bg-[#121721] border border-white/10 rounded-2xl p-5">
            <label className="font-mono text-xs uppercase tracking-widest text-zinc-400 block mb-3">Working title</label>
            <Input
              data-testid="export-document-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. The New Economics of Open-Source AI"
              className="bg-[#0F141C] border-white/10 text-zinc-100 h-11 rounded-xl focus-visible:ring-emerald-500 mb-4"
            />
            <label className="font-mono text-xs uppercase tracking-widest text-zinc-400 block mb-3">Direction (optional)</label>
            <Textarea
              data-testid="export-instructions-textarea"
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="e.g. Write an op-ed for a general audience, skeptical tone, ~1200 words."
              className="bg-[#0F141C] border-white/10 text-zinc-100 min-h-[90px] rounded-xl focus-visible:ring-emerald-500"
            />
          </div>

          <div className="bg-[#121721] border border-white/10 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <label className="font-mono text-xs uppercase tracking-widest text-zinc-400">Source clips</label>
              <span className="font-mono text-xs text-emerald-400">{selected.size} selected</span>
            </div>
            {loading ? (
              <div className="h-24 grid place-items-center"><Loader2 className="w-5 h-5 text-emerald-400 animate-spin" /></div>
            ) : favs.length === 0 ? (
              <div className="text-sm text-zinc-500 py-4">
                No favorites yet. <Link to="/feed" className="text-emerald-400 hover:text-emerald-300">Save some clips</Link> first.
              </div>
            ) : (
              <div className="space-y-2 max-h-[340px] overflow-y-auto pr-1">
                {favs.map((f) => {
                  const on = selected.has(f.clip_id);
                  return (
                    <button
                      key={f.clip_id}
                      data-testid="export-select-summary-checkbox"
                      onClick={() => toggle(f.clip_id)}
                      className={`w-full text-left flex items-start gap-3 p-3 rounded-xl border transition-colors ${
                        on ? "bg-emerald-500/10 border-emerald-500/40" : "bg-[#0F141C] border-white/10 hover:border-white/20"
                      }`}
                    >
                      <span className={`w-5 h-5 rounded-md grid place-items-center shrink-0 mt-0.5 border ${on ? "bg-emerald-500 border-emerald-500" : "border-white/20"}`}>
                        {on && <Check className="w-3.5 h-3.5 text-[#06231a]" strokeWidth={3} />}
                      </span>
                      <span>
                        <span className="block text-sm font-medium text-zinc-100 leading-snug line-clamp-2">{f.headline}</span>
                        <span className="block text-xs font-mono text-zinc-500 mt-0.5">{f.topic}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <Button
            data-testid="export-generate-article-button"
            onClick={generate}
            disabled={generating}
            className="w-full h-12 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-[#06231a] font-semibold transition-colors active:scale-[0.98]"
          >
            {generating ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
            Forge draft with Claude
          </Button>
        </div>

        {/* preview */}
        <div className="bg-[#121721] border border-white/10 rounded-2xl min-h-[500px] flex flex-col">
          <div className="flex items-center justify-between gap-3 p-4 border-b border-white/10 flex-wrap">
            <div className="flex items-center gap-2 text-zinc-300">
              <BookOpen className="w-4 h-4 text-emerald-400" />
              <span className="font-mono text-xs uppercase tracking-widest">Draft preview</span>
            </div>
            {doc && (
              <div className="flex items-center gap-2">
                <button onClick={copyMd} data-testid="export-copy-button" className="h-9 px-3 rounded-lg border border-white/10 text-zinc-300 text-sm flex items-center gap-1.5 hover:bg-white/5 transition-colors">
                  <Copy className="w-3.5 h-3.5" /> Copy
                </button>
                <button onClick={downloadMarkdown} data-testid="export-download-markdown-button" className="h-9 px-3 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-[#06231a] text-sm font-semibold flex items-center gap-1.5 transition-colors">
                  <Download className="w-3.5 h-3.5" /> .md
                </button>
                <button onClick={downloadPdf} data-testid="export-download-pdf-button" className="h-9 px-3 rounded-lg border border-white/10 text-zinc-200 text-sm flex items-center gap-1.5 hover:bg-white/5 transition-colors">
                  <FileType2 className="w-3.5 h-3.5" /> PDF
                </button>
              </div>
            )}
          </div>

          <div className="p-6 sm:p-8 flex-1 overflow-y-auto">
            {generating ? (
              <div className="h-full grid place-items-center text-center">
                <div>
                  <Loader2 className="w-8 h-8 text-emerald-400 animate-spin mx-auto mb-4" />
                  <p className="text-zinc-400 font-mono text-sm">Claude is drafting your document…</p>
                </div>
              </div>
            ) : doc ? (
              <motion.article initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="export-preview">
                <Markdown content={doc.content} />
              </motion.article>
            ) : (
              <div className="h-full grid place-items-center text-center">
                <div className="max-w-xs">
                  <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 grid place-items-center mx-auto mb-5">
                    <FileText className="w-7 h-7 text-emerald-400" />
                  </div>
                  <p className="text-zinc-400 leading-relaxed">
                    Pick your clips, set a title, and your Claude-forged draft appears here — ready to download and build on.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
